import * as React from "react";

import { format, getISOWeek, getISOWeekYear, subMonths } from "date-fns";
import { ChevronRight, Info } from "lucide-react";

import { Badge } from "src/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { numberFormat } from "src/lib/format";
import { cn } from "src/lib/utils";

function InfoTooltipTrigger({
  content,
  side = "top",
  delay,
  triggerClassName,
  iconClassName,
  contentClassName,
  as = "button",
  Icon = Info,
}) {
  const Comp = as;
  const isString = typeof content === "string";
  return (
    <Tooltip delayDuration={delay}>
      <TooltipTrigger asChild>
        <Comp
          type={as === "button" ? "button" : undefined}
          className={cn(
            "text-muted-foreground hover:text-foreground ml-1 inline-flex",
            triggerClassName
          )}
          aria-label={isString ? content : "More info"}
        >
          <Icon className={cn("size-4", iconClassName)} />
        </Comp>
      </TooltipTrigger>
      <TooltipContent side={side} className={contentClassName}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export const headerTooltip = (title, tooltip, options = {}) => {
  const {
    delay = 400,
    side,
    icon: Icon = Info,
    align = undefined,
    wrapperClassName,
    triggerClassName,
    iconClassName,
    contentClassName,
  } = options || {};

  const alignMap = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  };
  const justifyClass = align ? alignMap[align] : undefined;

  return () => (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        justifyClass,
        wrapperClassName
      )}
    >
      {title}
      <InfoTooltipTrigger
        content={tooltip}
        delay={delay}
        side={side}
        triggerClassName={triggerClassName}
        iconClassName={iconClassName}
        contentClassName={contentClassName}
        Icon={Icon}
      />
    </span>
  );
};

export const colorGroupColumn = {
  id: "groupColor",
  header: "",
  meta: {
    sticky: true,
    noSkeleton: true,
    className: "max-w-9 w-9 overflow-visible",
  },
  accessorKey: "color",
  cell: ({ row }) => {
    if (row.original?.meta?.colorDot) {
      const color = row.original.meta.colorDot;
      return (
        <div
          className="mx-auto flex size-2 items-center justify-center rounded-full"
          style={{ backgroundColor: color }}
        >
          <span className="sr-only">Color dot</span>
        </div>
      );
    }

    const depth = row.depth;
    const isParent = row.getCanExpand();
    const parentRow = row.getParentRow();
    const siblings = parentRow?.subRows || [];
    const rowIndex = siblings.indexOf(row);

    let color = row.original.meta?.color || parentRow?.original?.meta?.color;
    if (!color) return null;

    if (isParent && depth === 0) {
      return (
        <div className="flex justify-center">
          <ChevronRight
            className={cn("size-5", {
              "rotate-90": row.getIsExpanded(),
            })}
            style={{ color: color }}
          />
        </div>
      );
    }

    const isFirst = rowIndex === 0 && depth === 1;
    const isLast = rowIndex === siblings.length - 1;

    return (
      <div className="relative flex size-full w-full items-center justify-center">
        {(!isLast || depth !== 1) && (
          <div
            className={cn(
              "absolute -top-2 h-[calc(100%+1rem+1px)] w-1.5",
              isFirst && "shadow-[inset_0_4px_4px_-1px_rgba(0,0,0,0.25)]"
            )}
            style={{ backgroundColor: color }}
          />
        )}

        {isLast && depth === 1 && (
          <div
            className="absolute -top-2 h-[calc(50%+0.75rem)] w-1.5 rounded-b-full"
            style={{ backgroundColor: color }}
          />
        )}
      </div>
    );
  },
};

export const colorGroupLabelColumn = {
  header: "",
  id: "label",
  accessorKey: "label",
  meta: {
    sticky: true,
    rightBorder: true,
    noSkeleton: true,
    className: "min-w-[100px] left-[calc(var(--spacing)*9)] overflow-visible",
  },
  cell: ({ getValue, row }) => {
    // Take care of secondary nested rows
    if (row.depth > 0) {
      const color =
        row.original.meta?.color || row.getParentRow()?.original?.meta?.color;

      if (row.getCanExpand()) {
        return (
          <span className="flex items-center gap-4">
            <div className="flex justify-center">
              <ChevronRight
                className={cn("size-5", {
                  "rotate-90": row.getIsExpanded(),
                })}
                style={{ color: color }}
              />
            </div>
            <span className={cn(row.original?.meta?.bold && "font-bold")}>
              {getValue()}
            </span>
          </span>
        );
      }

      if (row.depth > 1) {
        const siblings = row.getParentRow()?.subRows || [];
        const rowIndex = siblings.indexOf(row);
        const isFirst = rowIndex === 0;
        const isLast = rowIndex === siblings.length - 1;

        return (
          <span className="relative flex items-center justify-start">
            {!isLast && (
              <div
                className={cn(
                  "absolute -top-[calc(var(--spacing)*3)] left-[calc(var(--spacing)*2-1px)] h-[calc(100%+1.5rem+1px)] w-1.5",
                  isFirst && "shadow-[inset_0_4px_4px_-1px_rgba(0,0,0,0.25)]"
                )}
                style={{ backgroundColor: color }}
              />
            )}

            {isLast && (
              <div
                className="absolute -top-[calc(var(--spacing)*3)] left-[calc(var(--spacing)*2-1px)] h-[calc(50%+0.75rem+4px)] w-1.5 rounded-b-full"
                style={{ backgroundColor: color }}
              />
            )}

            <div
              className={cn("ml-11", row.original?.meta?.bold && "font-bold")}
            >
              {getValue()}
            </div>
          </span>
        );
      }
    }

    const Icon = row.original.icon;
    const tooltipContent = row.original?.tooltip || row.original?.meta?.tooltip;
    const tooltipSide = row.original?.meta?.tooltipSide;

    return (
      <div className="flex items-center gap-2">
        {Icon && (
          <div className="bg-foreground rounded-full p-0.5">
            <Icon className="text-background size-4" />
          </div>
        )}
        <span className={cn(row.original?.meta?.bold && "font-bold")}>
          {getValue()}
        </span>
        {tooltipContent && (
          <InfoTooltipTrigger content={tooltipContent} side={tooltipSide} />
        )}
      </div>
    );
  },
};

// Function to transpose the data into the row based format
export const transposeDataToRows = (columns, data, dataFinder, rowMapper) => {
  // This function returns an object with the period as keys and the corresponding values from the data
  // using the provided accessor function or field name.
  const map = (accessor) =>
    columns.reduce((acc, col) => {
      const rowData = data.find((d) => dataFinder(d, col));
      if (!rowData) {
        acc[col] = 0; // Default to 0 if no data for the fieldName
        return acc;
      }

      if (accessor && typeof accessor === "function") {
        acc[col] = accessor(rowData);
      } else {
        acc[col] = rowData[accessor] || 0; // Default to 0 if field is missing
      }

      return acc;
    }, {});

  return rowMapper(map);
};

export const getRecentMonths = (count = 12) =>
  Array.from({ length: count }, (_, i) =>
    format(subMonths(new Date(), count - 1 - i), "yyyy-MM")
  );

export const getExcelExportData = (
  tableData,
  tableHeaders,
  labelFieldName = "label",
  currencyFormatter = numberFormat.currency
) => {
  const flattenRows = (rows) =>
    rows.flatMap(({ subRows = [], ...row }) => [row, ...flattenRows(subRows)]);

  return flattenRows(tableData).map((row) => {
    return tableHeaders.reduce((acc, header) => {
      const { accessorKey } = header;
      const format = header.format || row.format || currencyFormatter;
      let value = row[accessorKey];

      if (accessorKey === labelFieldName) {
        acc[accessorKey] = value == null ? "" : value;
        return acc;
      }

      if (typeof value === "function") value = value(row);

      if (value == null) {
        acc[accessorKey] = "";
        return acc;
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
          acc[accessorKey] = "";
          return acc;
        }
        const numericCandidate = +trimmed;
        if (!header.format && Number.isNaN(numericCandidate)) {
          acc[accessorKey] = value;
          return acc;
        }
        if (!Number.isNaN(numericCandidate)) value = numericCandidate;
      }

      if (typeof value === "number" && !Number.isFinite(value)) {
        acc[accessorKey] = "";
        return acc;
      }

      try {
        const formattedValue = format(value);
        const isBlankOutput =
          formattedValue == null ||
          (typeof formattedValue === "string" &&
            formattedValue.trim().toLowerCase() === "nan");
        acc[accessorKey] = isBlankOutput ? "" : formattedValue;
      } catch {
        acc[accessorKey] = value == null ? "" : value;
      }
      return acc;
    }, {});
  });
};

/**
 * Returns a formatted date header string based on the provided period and resolution.
 * @param {String} period - The period in the format "YYYY-MM" for month, "YYYY-WW" for week, or "YYYY-MM-DD" for day.
 * @param {String} resolution - The resolution of the date, can be "month", "week", or "day".
 * @param {Date} [dateFrom] - The starting date of the period. Optional.
 * @param {Date} [dateTo] - The ending date of the period. Optional.
 * @return {string} Formatted date header string based on the period and resolution.
 */
export const dateHeader = (period, resolution, dateFrom, dateTo) => {
  if (!period) {
    return "Invalid date";
  }

  switch (resolution) {
    case "month": {
      const baseDate = new Date(`${period}-02`).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });

      // Check the beginning of the month
      if (dateFrom && format(dateFrom, "yyyy-MM") === period) {
        const dayOfMonth = dateFrom.getDate();
        if (dayOfMonth > 1) {
          return baseDate + " (partial)";
        }
      }

      // Check the end of the month
      if (dateTo && format(dateTo, "yyyy-MM") === period) {
        const lastDayOfMonth = new Date(
          dateTo.getFullYear(),
          dateTo.getMonth() + 1,
          0
        ).getDate();
        if (dateTo.getDate() < lastDayOfMonth) {
          return baseDate + " (partial)";
        }
      }

      return baseDate;
    }
    case "week": {
      const [year, week] = period.split("-");
      const baseDate = `Week ${week}, ${year}`;

      // Check if dateFrom in the YYYY-WW format is the same as the period
      if (dateFrom) {
        const dateFromYear = getISOWeekYear(dateFrom);
        const dateFromWeek = getISOWeek(dateFrom);
        if (
          dateFromYear === parseInt(year) &&
          dateFromWeek === parseInt(week) &&
          dateFrom.getDay() !== 1
        ) {
          return baseDate + " (partial)";
        }
      }

      // Check if dateTo in the YYYY-WW format is the same as the period
      if (dateTo) {
        const dateToYear = getISOWeekYear(dateTo);
        const dateToWeek = getISOWeek(dateTo);
        if (
          dateToYear === parseInt(year) &&
          dateToWeek === parseInt(week) &&
          dateTo.getDay() !== 0
        ) {
          return baseDate + " (partial)";
        }
      }

      return baseDate;
    }
    case "day":
      return new Date(`${period}T00:00:00`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    default:
      return "Invalid resolution";
  }
};

export const styledDateHeader = (period, resolution, dateFrom, dateTo) => {
  const label = dateHeader(period, resolution, dateFrom, dateTo);
  const marker = " (partial)";
  if (!label || !label.endsWith(marker)) return label;

  const base = label.slice(0, -marker.length);
  return (
    <span>
      {base}
      <span className="text-muted-foreground">{marker}</span>
    </span>
  );
};

/**
 * Returns a stacked value component with top and bottom values.
 * @param {React.ReactNode} top - The top value to display.
 * @param {React.ReactNode} bottom - The bottom value to display.
 * @param {string} [className] - Optional additional class names for styling.
 * @return {React.ReactNode} A React component displaying the stacked values.
 */
export const stackedValue = (top, bottom, className) => {
  return (
    <div
      className={cn(
        "flex flex-col items-end text-right leading-tight",
        className
      )}
    >
      <div>{top}</div>
      <div className="group-data-[loading=false]:text-muted-foreground mt-0.5 text-xs">
        {bottom}
      </div>
    </div>
  );
};

/**
 * This function generates a cell renderer that displays a color gradient based on the values in the column.
 * It computes the background color based on the normalized value of the cell compared to the minimum and maximum values in the column.
 * It supports multiple colors for the gradient (up to 3), and can display the value as a badge or plain text.
 * @param {Object} options - Configuration options for the color gradient.
 * @param {Array|function} options.colors - An array of 1 to 3 color strings (CSS color values) or a function that returns such an array to define the gradient colors.
 * @param {Array|function} [options.domain] - An array of 2 or 3 numbers [min, [mid], max] or a function that returns such an array to define the domain for normalization.
 * @param {boolean} [options.asBadge=false] - Whether to display the value as a badge or plain text.
 * @param {boolean} [options.reverse=false] - Whether to reverse the gradient direction.
 * @param {string} [options.className] - Additional class names to apply to the cell.
 * @param {function} [options.format] - A function to format the value displayed in the cell.
 * @param {boolean} [options.coerceNonFinite=true] - Whether to coerce non-finite values (like NaN) to 0.
 * @param {number} [options.minOpacity=0] - The minimum opacity for the gradient, useful for fading to transparent.
 * @return {function} A cell renderer function that can be used in a table column definition.
 */
export const colorGradient = (options = {}) => {
  const {
    colors,
    domain,
    asBadge = false,
    reverse = false,
    className,
    format,
    coerceNonFinite = true,
    returnOnNonFinite = true,
    minOpacity = 0,
  } = options;

  // Normalize the value to a range between 0 and 1
  const normalizeValue = (currentValue, minimumValue, maximumValue) => {
    if (!isFinite(currentValue)) return 0;
    if (maximumValue === minimumValue) return 0.5;

    const normalized =
      (currentValue - minimumValue) / (maximumValue - minimumValue);
    return Math.min(1, Math.max(0, normalized));
  };

  // Mix two colors using the CSS color-mix function
  const mixTwoColors = (firstColor, secondColor, percentageFromSecondColor) =>
    `color-mix(in oklch, ${firstColor} ${100 - percentageFromSecondColor}%, ${secondColor} ${percentageFromSecondColor}%)`;

  // Fade a color to transparent using the CSS color-mix function
  const fadeToTransparent = (baseColor, percentage) => {
    const clampedPercentage = Math.max(minOpacity * 100, percentage);
    return `color-mix(in oklch, ${baseColor} ${clampedPercentage}%, transparent)`;
  };

  // Compute the background color based on VALUE + DOMAIN + COLORS
  // - Supports:
  //   * 1 color: opacity ramp
  //   * 2 colors: "meet" (each side fades to transparent toward midpoint)
  //   * 3 colors + 3-point domain: A→B for domain[0]→domain[1]; B→C for domain[1]→domain[2]
  //   * 3 colors + other domain: fallback to 50/50 split (A→B, then B→C)
  const computeBackgroundColor = (currentValue, resolvedDomain, colorList) => {
    const colorCount = colorList.length;

    // Reverse is applied by reflecting the value across the domain span
    const reflectIfReversed = (v, min, max) => (reverse ? max - (v - min) : v);

    // 1 color: opacity ramp vs transparent
    if (colorCount <= 1) {
      const [minVal, maxVal] =
        Array.isArray(resolvedDomain) && resolvedDomain.length >= 2
          ? [resolvedDomain[0], resolvedDomain[resolvedDomain.length - 1]]
          : [0, 1];

      const reflectedValue = reflectIfReversed(currentValue, minVal, maxVal);
      const normalized = normalizeValue(reflectedValue, minVal, maxVal);
      return fadeToTransparent(colorList[0], Math.round(normalized * 100));
    }

    // 2 colors: "meet" mode (each side fades to transparent toward the center)
    if (colorCount === 2) {
      const [minVal, maxVal] =
        Array.isArray(resolvedDomain) && resolvedDomain.length >= 2
          ? [resolvedDomain[0], resolvedDomain[resolvedDomain.length - 1]]
          : [0, 1];

      const reflectedValue = reflectIfReversed(currentValue, minVal, maxVal);
      const normalized = normalizeValue(reflectedValue, minVal, maxVal);

      if (normalized <= 0.5) {
        const percentage = Math.round((1 - normalized * 2) * 100); // 100 → 0 as it approaches midpoint
        return fadeToTransparent(colorList[0], percentage);
      } else {
        const percentage = Math.round((normalized - 0.5) * 2 * 100); // 0 → 100 after midpoint
        return fadeToTransparent(colorList[1], percentage);
      }
    }

    // 3 colors
    const [firstColor, secondColor, thirdColor] = colorList;

    // If domain returns 3 numbers: piecewise by real breakpoints
    if (Array.isArray(resolvedDomain) && resolvedDomain.length === 3) {
      let [min, mid, max] = resolvedDomain;

      // Ensure ascending order gracefully
      if (mid < min) [min, mid] = [mid, min];
      if (max < mid) [mid, max] = [max, mid];

      // Reflect value if reverse=true, across [min, max]
      const reflectedValue = reflectIfReversed(currentValue, min, max);

      if (reflectedValue <= mid) {
        // Segment A→B across [min, mid]
        const span = mid - min || 1;
        const t = Math.min(1, Math.max(0, (reflectedValue - min) / span));
        return mixTwoColors(firstColor, secondColor, Math.round(t * 100));
      } else {
        // Segment B→C across [mid, max]
        const span = max - mid || 1;
        const t = Math.min(1, Math.max(0, (reflectedValue - mid) / span));
        return mixTwoColors(secondColor, thirdColor, Math.round(t * 100));
      }
    }

    // Fallback: split at 50% (A→B then B→C) using normalized [min..max]
    const [minVal, maxVal] =
      Array.isArray(resolvedDomain) && resolvedDomain.length >= 2
        ? [resolvedDomain[0], resolvedDomain[resolvedDomain.length - 1]]
        : [0, 1];

    const reflectedValue = reflectIfReversed(currentValue, minVal, maxVal);
    const normalized = normalizeValue(reflectedValue, minVal, maxVal);

    if (normalized <= 0.5) {
      const percentage = Math.round((normalized / 0.5) * 100);
      return mixTwoColors(firstColor, secondColor, percentage);
    } else {
      const percentage = Math.round(((normalized - 0.5) / 0.5) * 100);
      return mixTwoColors(secondColor, thirdColor, percentage);
    }
  };

  // Resolve the domain to use for normalization or segmenting:
  // - Array of 2: [min, max]
  // - Array of 3 (only meaningful in 3-color mode): [a, b, c] → A→B on [a,b], B→C on [b,c]
  // - Function: ({ table, column, row }) => array
  // - Fallback: auto min/max over visible rows for this column
  const resolveDomain = (table, column, row) => {
    if (Array.isArray(domain) && domain.length >= 2) {
      return domain.slice(0, 3); // keep 2 or 3
    }

    if (typeof domain === "function") {
      try {
        const result = domain({ table, column, row });
        if (Array.isArray(result) && result.length >= 2) {
          return result.slice(0, 3);
        }
      } catch {
        // fall through to auto-domain
      }
    }

    // Auto-domain from visible rows, standard [min, max]
    const numericValues = table
      .getRowModel()
      .flatRows.map((row) => {
        const value = row.getValue(column.id);
        return typeof value === "number" && isFinite(value) ? value : undefined;
      })
      .filter((value) => value !== undefined);

    if (!numericValues.length) return [0, 1];
    return [Math.min(...numericValues), Math.max(...numericValues)];
  };

  return ({ getValue, table, column, row }) => {
    if (table.options.meta?.loading) return null;

    const resolvedDomain = resolveDomain(table, column, row);
    const cellColors =
      typeof colors === "function" ? colors({ table, column, row }) : colors;

    let currentValue = getValue();
    if (!isFinite(currentValue)) {
      if (coerceNonFinite) {
        currentValue = 0;
      } else if (returnOnNonFinite) {
        return null;
      }
    }

    let backgroundColor;
    if (!Array.isArray(cellColors)) {
      backgroundColor = cellColors;
    } else {
      // Compute background with the new domain logic (supports 3-point domain in 3-color mode)
      backgroundColor = computeBackgroundColor(
        currentValue,
        resolvedDomain,
        cellColors
      );
    }

    const formattedLabel =
      typeof format === "function"
        ? format(currentValue)
        : String(currentValue ?? "");

    // Determine the text color based on the normalized value and colors
    // Try to aim for a good contrast with the background color
    let textClass = "text-foreground";
    if (cellColors.length === 1) {
      // with single color, higher normalized opacity likely needs light text
      const [minVal, maxVal] =
        Array.isArray(resolvedDomain) && resolvedDomain.length >= 2
          ? [resolvedDomain[0], resolvedDomain[resolvedDomain.length - 1]]
          : [0, 1];
      const maybeReflected = reverse
        ? maxVal - (currentValue - minVal)
        : currentValue;
      const normalizedValue = normalizeValue(maybeReflected, minVal, maxVal);
      textClass =
        normalizedValue < 0.65
          ? "text-foreground"
          : "text-background dark:text-foreground";
    } else if (cellColors.length === 2) {
      const [minVal, maxVal] =
        Array.isArray(resolvedDomain) && resolvedDomain.length >= 2
          ? [resolvedDomain[0], resolvedDomain[resolvedDomain.length - 1]]
          : [0, 1];
      const maybeReflected = reverse
        ? maxVal - (currentValue - minVal)
        : currentValue;
      const normalized = normalizeValue(maybeReflected, minVal, maxVal);
      textClass =
        normalized > 0.2 && normalized < 0.8
          ? "text-foreground"
          : "text-background dark:text-foreground";
    }

    // Badge rendering
    if (asBadge) {
      return (
        <div
          className={cn("flex w-full items-center justify-center", className)}
        >
          <Badge className={textClass} style={{ backgroundColor }}>
            {formattedLabel}
          </Badge>
        </div>
      );
    }

    // Full cell rendering
    return (
      <div
        className={cn(
          "flex h-full w-[calc(100%+1px)] items-center justify-center",
          textClass,
          className
        )}
        style={{ backgroundColor }}
      >
        {formattedLabel}
      </div>
    );
  };
};
